/**
 * The brand this site serves.
 *
 * ── Why this is so much smaller than the source's ──────────────────────────
 * The file this was ported from (261 lines) resolved the brand FROM THE
 * HOSTNAME, because one deployment served two products from two CNAMEs onto
 * the same service. Tadbeer is one brand on its own deployment, so there is
 * nothing to resolve: the host-sniffing, the `?brand=` dev override and the
 * unknown-host fallback each had exactly one job, and that job does not exist
 * here. Keeping them would leave dead branches that look load-bearing — and
 * dead branches that look load-bearing are how the next reader wastes a day.
 *
 * What is KEPT is the SHAPE — `BrandIdentity` with the same fields — so the
 * pages ported alongside it did not have to be rewritten, and so a second
 * brand (if one ever lands here) is a new constant rather than a refactor.
 *
 * The same collapse was made for Watan Care first; this follows it.
 */

export type BrandKey = 'tadbeer';

/**
 * How much of the app a host may render. Tadbeer is `transactional` and only
 * that: this site exists to serve the links the mobile app and the operator's
 * SMS point at — pay, pay/return, confirm-location, terms. There is no web
 * catalogue, no web login and no bookings list, and there is no plan for one;
 * the app is the product.
 */
export type BrandSurface = 'transactional';

export interface BrandIdentity {
  key: BrandKey;
  /** Document title. Arabic first, matching the convention across the estate. */
  title: string;
  /** <meta name="description">. */
  description: string;
  /** Favicon under `public/`. `iconType` must match the file. */
  iconHref: string | null;
  iconType: string | null;
  /** `<meta name="theme-color">` — the browser chrome tint on Android/iOS. */
  themeColor: string;
  surface: BrandSurface;
  nameAr: string;
  nameEn: string;
  /**
   * Name on the Apple Pay sheet (`wpwlOptions.applePay.displayName`). The
   * customer reads this while authorising a charge, so it must be the brand
   * they think they are paying.
   */
  applePayDisplayName: string;
  /** Standalone logo under `public/`. */
  logoHref: string | null;
}

/**
 * Tadbeer's real brand values (2026-09-26) — no longer placeholders.
 *
 * `themeColor` is `#0051FF`, MEASURED from the logo served at
 * `tadbeerco.com/assets/images/tadbeer/favicons/favicon-96x96.png` rather than
 * guessed: the mark is two blues, `#0051FF` (the arc) and `#0596F1` (the
 * dots), and the strong one is the brand's voice.
 *
 * ⚠️ `iconHref` points at that SAME 96×96 file, copied into `public/`. That is
 * genuinely small — it is a favicon, and it is the largest Tadbeer artwork
 * published anywhere we can reach. It is correct as a browser-tab icon and it
 * is the honest thing to ship today; it is NOT a page logo.
 *
 * 🔴 `logoHref` stays `null` on purpose. `BrandMark` renders the NAME when
 * there is no logo file, which is deliberate: a 96px icon stretched across a
 * payment-page header would look broken, and looking broken on the page where
 * someone enters their card is worse than plain type. Ask the designer for an
 * SVG or a ≥512px PNG, drop it in `public/`, set this — nothing else changes.
 */
export const TADBEER: BrandIdentity = {
  key: 'tadbeer',
  title: 'تدبير · Tadbeer',
  // Factual and ungendered — Tadbeer serves men's and women's operations alike.
  description: 'تدبير — خدمات منزلية بالساعة: احجز زيارتك وأكّد موقعك وأتمم الدفع.',
  iconHref: '/tadbeer-icon.png',
  iconType: 'image/png',
  themeColor: '#0051FF', // the brand blue, measured from the logo
  surface: 'transactional',
  nameAr: 'تدبير',
  nameEn: 'Tadbeer',
  applePayDisplayName: 'Tadbeer',
  logoHref: null,
};

/** The brand. A function, not a bare constant, so callers read the same. */
export function resolveBrand(): BrandIdentity {
  return TADBEER;
}

export const brand = TADBEER;

/**
 * A CONSTANT, not a function — the ported pages use it as a bare condition
 * (`{isTransactionalBrand && <BrandMark/>}`), and a function there is always
 * truthy, so the branch would silently stop meaning anything.
 *
 * It is always true here. Kept rather than inlined so the pages read the same
 * as the ones they were ported from, and so the one place that would have to
 * change if a full web surface is ever added is this line.
 */
export const isTransactionalBrand = brand.surface === 'transactional';

/**
 * The API path segment.
 *
 * 🔴 THIS DECIDES WHICH MERCHANT ACCOUNT GETS THE MONEY. The backend derives a
 * contract's brand from the ROUTE CLASS it was created through —
 * `/mobile/tadbeer/...` is served by a controller whose seam says `'tadbeer'`.
 * No header changes that: the route IS the claim, and it decides which HyperPay
 * credentials the checkout uses and where a refund lands.
 *
 * ⚠️ The Watan Care port found every call still pointing at the SOURCE
 * product's namespace, with a green build throughout — a customer's payment
 * would have settled into another company's merchant account. Before deploying,
 * grep this repo for `/mobile/<other>/` and confirm each path exists under ours.
 */
export function apiBrandSegment(): BrandKey {
  return 'tadbeer';
}

/**
 * Stamp the document head and `data-brand` on <html>.
 *
 * Called once from `main.tsx` BEFORE React mounts: the tab title and the theme
 * colour are read by the browser while the bundle is still parsing, and a
 * customer opening an SMS link on a slow connection sees the head long before
 * the first paint.
 */
export function applyBrandOnLoad(): BrandIdentity {
  const b = TADBEER;
  if (typeof document === 'undefined') return b;

  document.title = b.title;
  document.documentElement.setAttribute('data-brand', b.key);

  const setMeta = (name: string, content: string) => {
    let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  setMeta('description', b.description);
  setMeta('theme-color', b.themeColor);

  if (b.iconHref) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      document.head.appendChild(link);
    }
    link.setAttribute('href', b.iconHref);
    if (b.iconType) link.setAttribute('type', b.iconType);
  }

  return b;
}
